import React, { useState, useEffect } from "react";
import { confirmResetPassword, confirmSignIn } from "@aws-amplify/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import toastr from "toastr";
import Layout from "../layout/Layout";

const ResetPasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || "";

  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isTemporaryPassword, setIsTemporaryPassword] = useState(false);
  const [tempUsername, setTempUsername] = useState("");
  const [tempSignInResult, setTempSignInResult] = useState(null);

  useEffect(() => {
    // Check if this is a temporary password flow
    const storedUsername = sessionStorage.getItem('tempUsername');
    const storedSignInResult = sessionStorage.getItem('tempSignInResult');
    
    if (storedUsername && storedSignInResult) {
      setIsTemporaryPassword(true);
      setTempUsername(storedUsername);
      setTempSignInResult(JSON.parse(storedSignInResult));
    }
  }, []);

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();

    if (isTemporaryPassword) {
      // Handle temporary password challenge
      if (!newPassword || !confirmPassword) {
        toastr.error("New password and confirmation are required");
        return;
      }

      if (newPassword !== confirmPassword) {
        toastr.error("Passwords do not match");
        return;
      }

      setLoading(true);
      try {
        await confirmSignIn({
          challengeResponse: newPassword
        });
        
        // Clear session storage
        sessionStorage.removeItem('tempUsername');
        sessionStorage.removeItem('tempSignInResult');
        
        toastr.success("Password updated successfully!");
        
        // Continue with login flow - fetch user data and redirect appropriately
        const { fetchUserAttributes } = await import('aws-amplify/auth');
        const userAttributes = await fetchUserAttributes();
        
        // Same logic as LoginPage - determine redirect based on user data
        const tenantID = userAttributes["custom:tenantID"];
        
        if (tenantID) {
          localStorage.setItem("tenantID", tenantID);
          navigate("/pricing");
        } else {
          navigate("/dashboard");
        }
      } catch (error) {
        toastr.error(error.message || "Failed to update password");
      } finally {
        setLoading(false);
      }
    } else {
      // Handle forgot password flow
      if (!code || !newPassword || !confirmPassword) {
        toastr.error("All fields are required");
        return;
      }

      if (newPassword !== confirmPassword) {
        toastr.error("Passwords do not match");
        return;
      }

      setLoading(true);
      try {
        await confirmResetPassword({
          username: email,
          confirmationCode: code,
          newPassword: newPassword,
        });
        toastr.success("Password reset successful!");
        navigate("/login");
      } catch (error) {
        toastr.error(error.message || "Failed to reset password");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Layout>
      <div className="position-absolute top-0 start-0 m-4 d-flex align-items-center">
        <img
          src="/images/odir-logo.png"
          alt="ODIR Logo"
          style={{ height: "40px", width: "auto" }}
        />
        <span className="ms-2 fw-bold" style={{ fontSize: "1.5rem", color: "#342ead" }}>ODIR</span>
      </div>
      <div className="container-fluid">
        <div className="row h-100">
          <div className="col-lg-8 col-md-6 col-sm-12 position-relative d-flex align-items-center">
            <div className="text-center w-100 px-5">
              <h1 className="display-4 fw-bold mb-4" style={{ color: "#342ead" }}>
                Reset Your Password
              </h1>
              <p className="lead fs-4 text-muted">
                Enter the verification code sent to {email}
              </p>
            </div>
          </div>

          <div className="col-lg-4 col-md-6 col-sm-12 right-section">
            <div className="form mx-auto">
              <h3>{isTemporaryPassword ? 'Set New Password' : 'Enter New Password'}</h3>
              <p>{isTemporaryPassword ? 'Please set a new password for your account' : 'Enter the code and your new password'}</p>
              <form onSubmit={handleConfirmReset}>
                {!isTemporaryPassword && (
                  <div className="mb-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Verification Code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                    />
                  </div>
                )}

                <div className="mb-3">
                  <div className="input-group">
                    <input
                      type={passwordVisible ? "text" : "password"}
                      className="form-control"
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={togglePasswordVisibility}
                    >
                      {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-Login-Signup w-100"
                  disabled={loading}
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </form>

              <p className="mt-4 text-center">
                Remember your password?{" "}
                <a href="/login" className="primary-text">
                  Back to Login
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ResetPasswordPage;
